import { useCallback, useEffect, useState } from "react";
import { FIELDIQ_API_URL, getToken } from "../lib/api";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  CheckCircle,
  ChevronDown,
  Eye,
  Loader2,
  Megaphone,
  Pin,
  RefreshCw,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";

const BROADCAST_REACTIONS = ["👍", "✅", "❓", "❤️"];

function bcTimeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function BroadcastCard({ broadcast, onArchive, onDelete, busy }) {
  const [expanded, setExpanded] = useState(false);
  const archived = Boolean(broadcast.archived_at);
  return (
    <div
      className={`rounded-xl border shadow-sm overflow-hidden ${
        archived
          ? "bg-slate-50 border-slate-200 border-dashed"
          : "bg-white border-slate-200"
      }`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {archived && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-slate-200 text-slate-600 rounded-full flex items-center gap-1">
                  <Archive className="w-3 h-3" /> Archived
                </span>
              )}
              {broadcast.priority === "urgent" && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-rose-100 text-rose-700 rounded-full flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Urgent
                </span>
              )}
              {broadcast.pinned_until && (
                <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                  <Pin className="w-3 h-3" /> Pinned
                </span>
              )}
              <span className="text-xs text-slate-400">
                {bcTimeAgo(broadcast.sent_at)}
              </span>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Users className="w-3 h-3" /> {broadcast.read_count ?? 0} read
              </span>
            </div>
            <p
              className={`text-sm font-semibold ${
                archived ? "text-slate-500" : "text-slate-800"
              }`}
            >
              {broadcast.title}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              Sent by {broadcast.sender_name}
            </p>
          </div>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        </div>
        {expanded && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {broadcast.body}
            </p>
            {/* Archive is the primary action and reads as reversible.
                Delete is deliberately quiet and text-only — it destroys the
                read receipts, so it should not look like the easy choice. */}
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
              <button
                onClick={() => onArchive(broadcast, !archived)}
                disabled={busy}
                className="px-3 py-1.5 border border-slate-300 hover:bg-slate-50 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                {archived ? (
                  <>
                    <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                  </>
                ) : (
                  <>
                    <Archive className="w-3.5 h-3.5" /> Archive
                  </>
                )}
              </button>
              <button
                onClick={() => onDelete(broadcast)}
                disabled={busy}
                className="px-2 py-1.5 text-xs text-slate-400 hover:text-rose-600 flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
              <span className="text-xs text-slate-400 ml-auto">
                {archived
                  ? "Hidden from FieldIQ. Reads kept."
                  : "Archiving hides it and keeps the read record."}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const BroadcastsPanel = ({ facility }) => {
  const fid = facility.facility_id;
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [pinDays, setPinDays] = useState("0");
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const authHeaders = () => {
    const tok = getToken();
    return {
      Authorization: `Bearer ${tok}`,
      "Content-Type": "application/json",
    };
  };

  const fetchBroadcasts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${FIELDIQ_API_URL}/broadcasts?ld_id=${encodeURIComponent(fid)}` +
          (showArchived ? "&include_archived=true" : ""),
        { headers: authHeaders() },
      );
      if (res.ok) {
        const data = await res.json();
        setBroadcasts([...(data.pinned || []), ...(data.feed || [])]);
      }
    } catch {}
    setLoading(false);
  }, [fid, showArchived]); // eslint-disable-line

  useEffect(() => {
    fetchBroadcasts();
  }, [fetchBroadcasts]);

  const handleArchive = async (broadcast, archived) => {
    setBusyId(broadcast.id);
    try {
      const res = await fetch(
        `${FIELDIQ_API_URL}/broadcasts/${broadcast.id}/archive`,
        {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify({ archived }),
        },
      );
      if (res.ok) {
        // When archived rows are hidden, an archived broadcast leaves the
        // list entirely — drop it locally rather than refetching just to
        // watch it disappear.
        if (archived && !showArchived) {
          setBroadcasts((prev) => prev.filter((b) => b.id !== broadcast.id));
        } else {
          fetchBroadcasts();
        }
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Could not ${archived ? "archive" : "restore"}: ${err.detail || res.status}`);
      }
    } catch {
      alert("Network error — nothing changed.");
    }
    setBusyId(null);
  };

  const handleDelete = async () => {
    const broadcast = confirmDelete;
    if (!broadcast) return;
    setBusyId(broadcast.id);
    try {
      const res = await fetch(`${FIELDIQ_API_URL}/broadcasts/${broadcast.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (res.ok) {
        setBroadcasts((prev) => prev.filter((b) => b.id !== broadcast.id));
        setConfirmDelete(null);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Delete failed: ${err.detail || res.status}`);
      }
    } catch {
      alert("Network error — broadcast not deleted.");
    }
    setBusyId(null);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const pinnedUntil =
        pinDays !== "0"
          ? new Date(Date.now() + Number(pinDays) * 86400000).toISOString()
          : null;
      const res = await fetch(`${FIELDIQ_API_URL}/broadcasts`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          ld_id: fid,
          title: title.trim(),
          body: body.trim(),
          priority,
          pinned_until: pinnedUntil,
        }),
      });
      if (res.ok) {
        setSentSuccess(true);
        setTitle("");
        setBody("");
        setPriority("normal");
        setPinDays("0");
        setShowConfirm(false);
        setTimeout(() => setSentSuccess(false), 4000);
        fetchBroadcasts();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Send failed: ${err.detail || res.status}`);
      }
    } catch {
      alert("Network error — broadcast not sent.");
    }
    setSending(false);
  };

  const canSend = title.trim().length > 0 && body.trim().length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="w-5 h-5 text-violet-600" />
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              Broadcasts
            </h2>
            <p className="text-sm text-slate-500">
              Send announcements to all users. One-way, no replies.
            </p>
          </div>
        </div>
        <button
          onClick={fetchBroadcasts}
          disabled={loading}
          className="px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {sentSuccess && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          Broadcast sent — all users in {fid} will receive a push notification.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Compose */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700">
            Compose Broadcast
          </h3>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              placeholder="Brief, clear subject line..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Message
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Write your announcement here. Be clear and actionable. Users cannot reply."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-violet-500"
            />
            <p className="text-xs text-slate-400 mt-1">{body.length} chars</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
              >
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Pin for
              </label>
              <select
                value={pinDays}
                onChange={(e) => setPinDays(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
              >
                <option value="0">No pin</option>
                <option value="1">1 day</option>
                <option value="3">3 days</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPreview(true)}
              disabled={!canSend}
              className="flex-1 px-4 py-2.5 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button
              onClick={() => setShowConfirm(true)}
              disabled={!canSend}
              className="flex-1 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <Send className="w-4 h-4" /> Send Broadcast
            </button>
          </div>
          <p className="text-xs text-slate-400 text-center">
            Broadcasts cannot be edited after sending. Reactions:{" "}
            {BROADCAST_REACTIONS.join(" ")}
          </p>
        </div>

        {/* Sent list */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-700">
              Sent Broadcasts
            </h3>
            <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="rounded border-slate-300"
              />
              Show archived
            </label>
          </div>
          {loading ? (
            <div className="text-center py-8 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
          ) : broadcasts.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 text-sm">
              {showArchived
                ? "No broadcasts sent yet."
                : "No active broadcasts. Tick “Show archived” if you are looking for an older one."}
            </div>
          ) : (
            <div className="space-y-2">
              {broadcasts.map((b) => (
                <BroadcastCard
                  key={b.id}
                  broadcast={b}
                  onArchive={handleArchive}
                  onDelete={setConfirmDelete}
                  busy={busyId === b.id}
                />
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400">
            Expand a broadcast to archive or delete it. Archiving removes it
            from FieldIQ and can be undone; deleting also destroys who read it.
          </p>
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-slate-900 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                {priority === "urgent" && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-rose-900/60 text-rose-300 rounded-full">
                    Urgent
                  </span>
                )}
                {pinDays !== "0" && (
                  <span className="px-2 py-0.5 text-xs bg-amber-900/50 text-amber-300 rounded-full flex items-center gap-1">
                    <Pin className="w-3 h-3" /> Pinned {pinDays}d
                  </span>
                )}
              </div>
              <p className="text-white font-semibold">{title || "Untitled"}</p>
              <p className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">
                {body}
              </p>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
                {BROADCAST_REACTIONS.map((r) => (
                  <span key={r} className="text-lg opacity-60">
                    {r}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPreview(false)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setShowConfirm(true);
                }}
                className="flex-1 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium"
              >
                Looks good — Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Confirm Send</h3>
                <p className="text-sm text-slate-500">
                  This cannot be edited or recalled after sending.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm text-slate-700 space-y-1">
              <p>
                <span className="text-slate-400">To:</span> All users in {fid}
              </p>
              <p>
                <span className="text-slate-400">Subject:</span> {title}
              </p>
              <p>
                <span className="text-slate-400">Priority:</span> {priority}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex-1 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm — names what is lost, and offers archive as the way out */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">
                  Delete permanently?
                </h3>
                <p className="text-sm text-slate-500">This cannot be undone.</p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm text-slate-700">
              <p className="font-medium">{confirmDelete.title}</p>
              <p className="text-xs text-slate-500 mt-1">
                Deleting also removes {confirmDelete.read_count ?? 0} read
                receipt{(confirmDelete.read_count ?? 0) === 1 ? "" : "s"} and
                every reaction. Archive instead if you only want it out of the
                FieldIQ feed.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const b = confirmDelete;
                  setConfirmDelete(null);
                  handleArchive(b, true);
                }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm flex items-center justify-center gap-1.5 hover:bg-slate-50"
              >
                <Archive className="w-4 h-4" /> Archive
              </button>
              <button
                onClick={handleDelete}
                disabled={busyId === confirmDelete.id}
                className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {busyId === confirmDelete.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Admin Panel — sub-tabs: Users | Analytics | API Keys | Audit Log ───
